// Mon compte : profil (nom, prénoms, courriel, canal préféré), état de l'identité (déclarée ou
// vérifiée au guichet), accès aux pièces, représentations et notifications, déconnexion.
import { Bandeau, Bouton, Champ, Formulaire } from '@cc/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { requete } from '../../../api/client';
import { date } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { useCollectiviteChoisie } from '../collectivite';
import { EnTeteCitoyen, ExigeConnexion, Ligne, Liste, Section, TexteStatut } from '../commun';
import { CLE_PROFIL_USAGER, useSessionUsager } from '../session/FournisseurSessionUsager';
import type { ProfilUsager } from '../types';
import styles from '../citoyen.module.css';

export const LIBELLES_PIECE_IDENTITE: Record<string, string> = {
  carte_nationale_identite: 'carte nationale d\'identité',
  passeport: 'passeport',
  titre_sejour: 'titre de séjour',
  permis_conduire: 'permis de conduire',
  acte_naissance: 'acte de naissance',
  autre: 'autre pièce',
};

function FormulaireProfil({ profil }: { profil: ProfilUsager }) {
  const [nom, setNom] = useState(profil.nom);
  const [prenoms, setPrenoms] = useState(profil.prenoms ?? '');
  const [courriel, setCourriel] = useState(profil.courriel ?? '');
  const [canal, setCanal] = useState<'sms' | 'courriel' | ''>(profil.canal_prefere ?? '');
  useEffect(() => {
    setNom(profil.nom);
    setPrenoms(profil.prenoms ?? '');
    setCourriel(profil.courriel ?? '');
    setCanal(profil.canal_prefere ?? '');
  }, [profil]);
  const identiteChange = nom.trim() !== profil.nom || prenoms.trim() !== (profil.prenoms ?? '');
  const enregistrement = useAction({
    executer: () =>
      requete<ProfilUsager>('/usager/profil', {
        methode: 'PUT',
        corps: {
          ...(nom.trim() !== profil.nom ? { nom: nom.trim() } : {}),
          ...(prenoms.trim() !== (profil.prenoms ?? '') ? { prenoms: prenoms.trim() } : {}),
          ...(courriel.trim() !== (profil.courriel ?? '') ? { courriel: courriel.trim() || null } : {}),
          ...(canal !== (profil.canal_prefere ?? '') ? { canal_prefere: canal || null } : {}),
        },
      }),
    succes: 'Profil enregistré.',
    invalider: [[...CLE_PROFIL_USAGER]],
  });
  const erreurChamp = (champ: string) => enregistrement.erreursChamps.find((e) => e.champ === champ)?.message ?? null;
  return (
    <Formulaire
      libelle="Profil"
      onSoumettre={() => enregistrement.lancer(undefined)}
      erreurs={enregistrement.erreursChamps.map((e) => ({ champ: `profil-${e.champ.replace(/_/g, '-')}`, message: e.message }))}
      actions={
        <Bouton type="submit" variante="secondaire" chargement={enregistrement.enCours}>
          Enregistrer le profil
        </Bouton>
      }
    >
      {enregistrement.messageErreur && enregistrement.erreursChamps.length === 0 ? <Bandeau type="erreur">{enregistrement.messageErreur}</Bandeau> : null}
      <Champ id="profil-identifiant" libelle="Identifiant de connexion" value={profil.identifiant} readOnly aide={profil.telephone ? `Téléphone : ${profil.telephone}` : 'Aucun téléphone enregistré'} />
      <Champ id="profil-nom" libelle="Nom" value={nom} onChange={(e) => setNom(e.target.value)} erreur={erreurChamp('nom')} required />
      <Champ id="profil-prenoms" libelle="Prénoms" value={prenoms} onChange={(e) => setPrenoms(e.target.value)} erreur={erreurChamp('prenoms')} facultatif />
      <Champ id="profil-courriel" libelle="Courriel" type="email" value={courriel} onChange={(e) => setCourriel(e.target.value)} erreur={erreurChamp('courriel')} facultatif />
      <fieldset className={styles.pile} id="profil-canal-prefere">
        <legend>Canal préféré pour les notifications</legend>
        <div className={styles.ligneBoutons}>
          <label>
            <input type="radio" name="canal" value="sms" checked={canal === 'sms'} disabled={!profil.telephone} onChange={() => setCanal('sms')} /> SMS
          </label>
          <label>
            <input type="radio" name="canal" value="courriel" checked={canal === 'courriel'} disabled={!courriel.trim()} onChange={() => setCanal('courriel')} /> Courriel
          </label>
          <label>
            <input type="radio" name="canal" value="" checked={canal === ''} onChange={() => setCanal('')} /> Selon la démarche
          </label>
        </div>
        {erreurChamp('canal_prefere') ? <p className={styles.statutAttention}>{erreurChamp('canal_prefere')}</p> : null}
      </fieldset>
      {identiteChange && profil.identite_verifiee ? (
        <Bandeau type="attention">Changer le nom ou les prénoms annule la vérification de votre identité faite au guichet.</Bandeau>
      ) : null}
    </Formulaire>
  );
}

export function PageCompte() {
  const { profil, deconnecter } = useSessionUsager();
  const [choisie] = useCollectiviteChoisie();
  const navigate = useNavigate();
  return (
    <>
      <EnTeteCitoyen titre="Mon compte" sousTitre="Un seul compte pour toutes les collectivités." />
      <ExigeConnexion>
        {profil ? (
          <div className={styles.pile}>
            <Section titre="Identité">
              <Liste>
                <Ligne
                  href="/citoyen/compte/identite"
                  titre={profil.identite_verifiee ? 'Identité vérifiée au guichet' : 'Identité déclarée, non vérifiée'}
                  sousTitre={
                    profil.identite_verifiee
                      ? `Le ${date(profil.identite_verifiee.le)}${profil.identite_verifiee.collectivite ? ` à ${profil.identite_verifiee.collectivite}` : ''}, sur ${LIBELLES_PIECE_IDENTITE[profil.identite_verifiee.type_piece] ?? profil.identite_verifiee.type_piece}`
                      : 'Ce que change la vérification, et vos informations déclarées'
                  }
                  droite={<TexteStatut libelle={profil.identite_verifiee ? 'Vérifiée' : 'Déclarée'} ton={profil.identite_verifiee ? 'ok' : 'neutre'} />}
                />
              </Liste>
            </Section>
            <Section titre="Mon espace">
              <Liste>
                <Ligne href="/citoyen/compte/representations" titre="Représentations" sousTitre="Agir au nom d'une entreprise ou d'une association" />
                <Ligne href="/citoyen/quartiers" titre="Mon quartier" sousTitre="Espace des représentants de quartier désignés" />
                <Ligne href="/citoyen/compte/pieces" titre="Mes pièces" sousTitre="Coffre de pièces réutilisables" />
                <Ligne href="/citoyen/notifications" titre="Notifications et canaux" sousTitre="SMS, courriel, portail" />
                <Ligne href="/citoyen/rendez-vous" titre="Mes rendez-vous" />
                <Ligne href="/citoyen/reclamations" titre="Mes réclamations" />
              </Liste>
            </Section>
            <Section titre="Profil">
              <FormulaireProfil profil={profil} />
            </Section>
            <div>
              <Bouton variante="secondaire" onClick={() => void deconnecter().then(() => navigate(choisie ? `/citoyen/${choisie.code}` : '/citoyen', { replace: true }))}>
                Se déconnecter
              </Bouton>
            </div>
          </div>
        ) : null}
      </ExigeConnexion>
    </>
  );
}
